import { z } from 'zod';
import { evaluateCondition, isEmptyAnswer } from './condition.js';
import { allFields, isDisplayFieldType, } from './definition.js';
import { fieldPattern, FIELD_PATTERNS, FORM_LIMITS, ISO_DATE } from './primitives.js';
/**
 * Answers to a form definition, and the pure functions that decide what is visible, what
 * is valid and what must be encrypted (FR-INTAKE-01/03/04).
 *
 * Nothing here touches React, the network or the clock: the renderer (M8.4) and the API
 * (M8.2) both import these functions so a patient can never be shown one set of rules and
 * judged by another (rules §17.2 - the browser's copy is a convenience, the API's is the
 * enforcement).
 */
export const FileRefSchema = z.object({
    /** `files.id`. The object itself is only reachable through a signed URL (§28.7). */
    fileId: z.string().min(1).max(64),
    /** Display name only. Never used as a storage path (rules §28.4). */
    name: z.string().max(255).optional(),
    contentType: z.string().max(160).optional(),
    sizeBytes: z.number().int().min(0).optional(),
});
/**
 * A typed-name signature (ADR-017): the patient types their name and the renderer stamps
 * the moment they agreed. No drawing canvas - an image adds nothing legally and is one
 * more blob of PHI to protect.
 */
export const SignatureAnswerSchema = z.object({
    name: z.string().trim().min(2).max(120),
    consentedAt: z.iso.datetime(),
});
export const AnswerValueSchema = z.union([
    z.string().max(FORM_LIMITS.maxTextareaAnswer),
    z.number(),
    z.boolean(),
    z.array(z.string().max(200)).max(FORM_LIMITS.maxOptions),
    z.array(FileRefSchema).max(FORM_LIMITS.maxFilesPerField),
    SignatureAnswerSchema,
    z.null(),
]);
/** `intake_submissions.answers` keyed by field id. */
export const AnswerSetSchema = z.record(z.string().max(64), AnswerValueSchema);
/**
 * Resolves which sections and fields are on screen.
 *
 * Conditions may chain - question C depends on B which depends on A - so this iterates to
 * a fixed point rather than making one pass, with a hidden field read as empty. The loop
 * is bounded by the field count, so a definition with a dependency cycle settles instead
 * of hanging; a cycle simply resolves to "hidden", which is the safe answer for a question
 * nobody can reach.
 */
export function resolveVisibility(definition, answers) {
    const fields = allFields(definition);
    let visibleFieldIds = new Set(fields.map((field) => field.id));
    let visibleSectionIds = new Set(definition.sections.map((section) => section.id));
    const rounds = Math.min(fields.length + 2, 64);
    for (let round = 0; round < rounds; round += 1) {
        const currentFields = visibleFieldIds;
        const read = (fieldId) => currentFields.has(fieldId) ? answers[fieldId] : undefined;
        const nextSectionIds = new Set();
        const nextFieldIds = new Set();
        for (const section of definition.sections) {
            const sectionVisible = section.visibleWhen
                ? evaluateCondition(section.visibleWhen, read)
                : true;
            if (!sectionVisible)
                continue;
            nextSectionIds.add(section.id);
            for (const field of section.fields) {
                if (field.visibleWhen && !evaluateCondition(field.visibleWhen, read))
                    continue;
                nextFieldIds.add(field.id);
            }
        }
        const stable = nextFieldIds.size === visibleFieldIds.size &&
            nextSectionIds.size === visibleSectionIds.size &&
            [...nextFieldIds].every((id) => visibleFieldIds.has(id)) &&
            [...nextSectionIds].every((id) => visibleSectionIds.has(id));
        visibleFieldIds = nextFieldIds;
        visibleSectionIds = nextSectionIds;
        if (stable)
            break;
    }
    return { sectionIds: visibleSectionIds, fieldIds: visibleFieldIds };
}
/** Visible fields in document order, display fields included. */
export function visibleFields(definition, answers) {
    const visibility = resolveVisibility(definition, answers);
    return definition.sections
        .filter((section) => visibility.sectionIds.has(section.id))
        .flatMap((section) => section.fields.filter((field) => visibility.fieldIds.has(field.id)));
}
/** Visible sections, each narrowed to its visible fields. Drives the renderer's pages. */
export function visibleSections(definition, answers) {
    const visibility = resolveVisibility(definition, answers);
    return definition.sections
        .filter((section) => visibility.sectionIds.has(section.id))
        .map((section) => ({
        ...section,
        fields: section.fields.filter((field) => visibility.fieldIds.has(field.id)),
    }))
        .filter((section) => section.fields.length > 0);
}
/* -------------------------------------------------------------------------- */
/* Sensitivity                                                                 */
/* -------------------------------------------------------------------------- */
/** Fields whose answers belong in `intake_submissions.answers_sensitive_enc`. */
export function sensitiveFieldIds(definition) {
    return allFields(definition)
        .filter((field) => 'sensitive' in field && field.sensitive === true)
        .map((field) => field.id);
}
/** The marker written in place of a sensitive answer. */
export const REDACTED = '[redacted]';
/**
 * A copy of the answers safe to log or attach to an error report (rules §25, §32).
 * Sensitive answers become `[redacted]`; everything else is left alone so a support
 * engineer can still see which branch the patient was on.
 */
export function redactAnswers(definition, answers) {
    const sensitive = new Set(sensitiveFieldIds(definition));
    const output = {};
    for (const [fieldId, value] of Object.entries(answers)) {
        if (value === undefined)
            continue;
        output[fieldId] = sensitive.has(fieldId) ? REDACTED : value;
    }
    return output;
}
/**
 * Splits answers the way the two columns are stored (DATABASE.md §7.8): the plain half
 * goes to `answers`, the sensitive half to `answers_sensitive_enc`. The API owns the
 * encryption; this owns the decision about which side a field falls on.
 */
export function splitAnswersBySensitivity(definition, answers) {
    const sensitive = new Set(sensitiveFieldIds(definition));
    const plain = {};
    const secret = {};
    for (const [fieldId, value] of Object.entries(answers)) {
        if (value === undefined)
            continue;
        if (sensitive.has(fieldId))
            secret[fieldId] = value;
        else
            plain[fieldId] = value;
    }
    return { answers: plain, sensitiveAnswers: secret };
}
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function asStringList(value) {
    if (!Array.isArray(value))
        return null;
    return value.every((item) => typeof item === 'string') ? value : null;
}
function isRealDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
function checkText(field, value, max) {
    if (value.length > max)
        return `Must be ${max} characters or fewer.`;
    const validation = 'validation' in field ? field.validation : undefined;
    if (validation?.minLength !== undefined && value.length < validation.minLength) {
        return `Must be at least ${validation.minLength} characters.`;
    }
    if (validation?.maxLength !== undefined && value.length > validation.maxLength) {
        return `Must be ${validation.maxLength} characters or fewer.`;
    }
    if (validation?.pattern !== undefined && !fieldPattern(validation.pattern).test(value)) {
        return validation.patternMessage ?? FIELD_PATTERNS[validation.pattern].message;
    }
    return null;
}
function checkOptions(field, selected) {
    if (!('options' in field))
        return null;
    const allowed = new Set(field.options.map((option) => option.value));
    if (selected.some((value) => !allowed.has(value)))
        return 'Choose one of the listed answers.';
    const validation = 'validation' in field ? field.validation : undefined;
    if (validation?.minSelected !== undefined && selected.length < validation.minSelected) {
        return `Choose at least ${validation.minSelected}.`;
    }
    if (validation?.maxSelected !== undefined && selected.length > validation.maxSelected) {
        return `Choose ${validation.maxSelected} at most.`;
    }
    return null;
}
/** The single source of the wording a patient sees under a field. */
function validateValue(field, value) {
    switch (field.type) {
        case 'text':
            return typeof value === 'string'
                ? checkText(field, value, FORM_LIMITS.maxTextAnswer)
                : 'Enter a value.';
        case 'textarea':
            return typeof value === 'string'
                ? checkText(field, value, FORM_LIMITS.maxTextareaAnswer)
                : 'Enter a value.';
        case 'email':
            if (typeof value !== 'string')
                return 'Enter an email address.';
            return EMAIL.test(value) ? null : 'Enter a valid email address.';
        case 'phone': {
            if (typeof value !== 'string')
                return 'Enter a phone number.';
            const digits = value.replace(/\D/g, '');
            return digits.length >= 10 && digits.length <= 15 ? null : 'Enter a valid phone number.';
        }
        case 'number': {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return 'Enter a number.';
            const validation = field.validation;
            if (validation?.min !== undefined && value < validation.min)
                return `Must be ${validation.min} or more.`;
            if (validation?.max !== undefined && value > validation.max)
                return `Must be ${validation.max} or less.`;
            return null;
        }
        case 'date': {
            if (typeof value !== 'string' || !ISO_DATE.test(value) || !isRealDate(value)) {
                return 'Enter a valid date.';
            }
            const validation = field.validation;
            if (validation?.minDate !== undefined && value < validation.minDate)
                return 'Choose a later date.';
            if (validation?.maxDate !== undefined && value > validation.maxDate)
                return 'Choose an earlier date.';
            return null;
        }
        case 'select': {
            if (field.multiple === true) {
                const selected = asStringList(value);
                return selected === null ? 'Choose from the list.' : checkOptions(field, selected);
            }
            return typeof value === 'string' ? checkOptions(field, [value]) : 'Choose from the list.';
        }
        case 'radio':
            return typeof value === 'string' ? checkOptions(field, [value]) : 'Choose an answer.';
        case 'checkboxGroup': {
            const selected = asStringList(value);
            return selected === null ? 'Choose at least one answer.' : checkOptions(field, selected);
        }
        case 'checkbox':
            if (typeof value !== 'boolean')
                return 'Tick or leave blank.';
            return field.required === true && value !== true ? 'Please tick this box to continue.' : null;
        case 'yesNo':
            return typeof value === 'boolean' ? null : 'Choose yes or no.';
        case 'consent':
            return value === true ? null : 'Please agree to continue.';
        case 'signature': {
            const parsed = SignatureAnswerSchema.safeParse(value);
            return parsed.success ? null : 'Type your full name to sign.';
        }
        case 'file': {
            const parsed = z.array(FileRefSchema).safeParse(value);
            if (!parsed.success)
                return 'Upload a file.';
            if (parsed.data.length > FORM_LIMITS.maxFilesPerField) {
                return `Attach ${FORM_LIMITS.maxFilesPerField} files at most.`;
            }
            if (field.multiple !== true && parsed.data.length > 1)
                return 'Attach one file.';
            return null;
        }
        default:
            return null;
    }
}
/** `consent` and a required `checkbox` are unanswered until they are ticked. */
function isAnswered(field, value) {
    if (value === undefined || isEmptyAnswer(value))
        return false;
    if (field.type === 'consent' || field.type === 'checkbox')
        return value === true;
    return true;
}
function cleanValue(value) {
    if (typeof value === 'string')
        return value.trim();
    if (Array.isArray(value)) {
        return value.map((item) => typeof item === 'string' ? item.trim() : item);
    }
    return value;
}
/**
 * Validates a set of answers against a definition.
 *
 * Only *visible* fields are considered: a hidden question is never required and its answer
 * is dropped from `values`, so the stored submission always matches the branch the patient
 * actually saw. Unknown field ids are dropped rather than rejected, because a draft saved
 * against an older version may carry keys this version no longer has (M8.7).
 */
export function validateAnswers(definition, answers, options = {}) {
    const partial = options.partial === true;
    const visibility = resolveVisibility(definition, answers);
    const errors = {};
    const values = {};
    for (const field of allFields(definition)) {
        if (isDisplayFieldType(field.type))
            continue;
        if (!visibility.fieldIds.has(field.id))
            continue;
        const raw = answers[field.id];
        const answered = isAnswered(field, raw);
        if (!answered) {
            const required = 'required' in field && field.required === true;
            if (required && !partial) {
                errors[field.id] =
                    field.type === 'consent' || field.type === 'checkbox'
                        ? 'Please tick this box to continue.'
                        : 'This answer is required.';
            }
            continue;
        }
        const value = cleanValue(raw);
        const message = validateValue(field, value);
        if (message !== null) {
            errors[field.id] = message;
            continue;
        }
        values[field.id] = value;
    }
    return { valid: Object.keys(errors).length === 0, errors, values };
}
//# sourceMappingURL=answers.js.map